import type { DraggableProvided } from '@hello-pangea/dnd'
import type { RankingItem } from '@vedalib/editor/lib/elements'
import { rtValueToText, textToRtValue } from '@vedalib/rich-text'
import type { RichTextValue } from '@vedalib/rich-text'
import cn from 'classnames'
import * as R from 'ramda'
import React from 'react'

import TestFeedback from 'components/Editor/EditorElements/courseKit/TestFeedback'
import { getFeedbackValue } from 'components/Editor/EditorElements/utils/getFeedbackValue'
import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import RichText from 'components/form/RichText'
import { useGetRichTextProps } from 'components/form/RichText/useGetRichTextProps'
import { IconButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import { KitSize } from 'components/uiKit/KitTypes'
import { PreviewMode } from 'services/Store/Project/enums'
import { t } from 'services/Translation'

import type { TestRankingElementType } from '../testRankingTypes'
import * as s from './TestRankingElement.module.scss'
import TestRankingItem from './TestRankingItem'

interface ITestRankingElementEditorItemProps
  extends Pick<
    React.ComponentProps<TestRankingElementType>,
    'element' | 'mode' | 'styles' | 'waiting' | 'onChange' | 'font' | 'feedback'
  > {
  index: number
  provided: DraggableProvided
  showControls?: boolean
  isDragging: boolean
}

const TestRankingElementEditorItem: React.FC<ITestRankingElementEditorItemProps> = ({
  provided,
  index,
  element,
  mode,
  styles,
  waiting,
  font,
  onChange,
  feedback,
  isDragging,
  showControls,
}) => {
  const { id: elementId, value } = element
  const { items } = value
  const feedbackValue = getFeedbackValue(value)

  const { isActiveElement, activeIndex, onLabelSelect, isFill } = useGetRichTextProps({
    elementId,
    mode,
  })

  const onChangeLabel = (labelValue: RichTextValue, index: number) =>
    onChange?.(R.assocPath(['items', index, 'label'], labelValue, value))

  const handleDelete = (item: RankingItem) => () =>
    onChange?.(
      R.assoc(
        'items',
        value.items.filter(({ value: id }) => id !== item.value),
        value,
      ),
    )

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      className={cn(s.wrapItem, { [s.active]: activeIndex === index.toString() })}
      key={items[index].value}
      style={{
        ...provided.draggableProps.style,
        marginBottom: styles.offset.marginTop,
      }}
    >
      <TestFeedback
        feedbackValue={feedbackValue}
        font={font}
        mode={mode}
        styles={feedback}
        isTrueAnswer
        showAnswerFb
      >
        <TestRankingItem
          dragHandleProps={provided.dragHandleProps}
          font={font}
          index={index}
          isDragging={isDragging}
          label={rtValueToText(items[index].label)}
          styles={styles}
        >
          <div className={s.text} onMouseDown={() => onLabelSelect(index.toString())}>
            <RichText
              active={isActiveElement && activeIndex === index.toString()}
              disabled={!isFill}
              isPreview={mode?.previewMode !== PreviewMode.editor}
              name={`rankingItem.${index}`}
              onChange={(val) => onChangeLabel(val, index)}
              styles={font}
              value={items[index].label || textToRtValue('')}
              waiting={waiting}
            />
          </div>
        </TestRankingItem>
        {isFill && isActiveElement && showControls && (
          <AbsolutePortal
            name='deleteMultipleChoiceItem'
            placement='right'
            translateX='3px'
            translateY='-50%'
          >
            <Tooltip overlay={t('uiKit.tooltip.deleteAnswer')}>
              <IconButton
                disabled={items.length < 3}
                icon='otherClose'
                name='deleteMultipleChoiceItem'
                onClick={handleDelete(items[index])}
                size={KitSize.XS}
                styleType='secondary'
              />
            </Tooltip>
          </AbsolutePortal>
        )}
      </TestFeedback>
    </div>
  )
}

export default TestRankingElementEditorItem
